import React, { useCallback, useMemo, useState } from "react";
import axios from "axios";
import { Alert, Form, Modal, Spinner } from "react-bootstrap";
import { Gear, Robot, ShieldLock } from "react-bootstrap-icons";

const API_BASE = process.env.REACT_APP_API || "";
const ENDPOINT_GET_SETTINGS = `${API_BASE}/getAllSetting`;
const ENDPOINT_UPDATE_SETTING = `${API_BASE}/updateSetting`;
const ENDPOINT_GET_PDPA = `${API_BASE}/getPdpaProtection`;
const ENDPOINT_UPDATE_PDPA = `${API_BASE}/updatePdpaProtection`;

function pickLearnDoSetting(settingsArray) {
  const arr = Array.isArray(settingsArray) ? settingsArray : [];
  if (!arr.length) return null;

  const normalize = (v) =>
    String(v || "")
      .toLowerCase()
      .replace(/\s+/g, "");

  const candidates = arr.filter((r) =>
    [r.module, r.module_name, r.name, r.moduleCode, r.code].some((v) => {
      const s = normalize(v);
      return (
        s.includes("learndo") ||
        s.includes("learn+do") ||
        (s.includes("learn") && s.includes("do"))
      );
    })
  );

  return candidates[0] || arr[0];
}

function getUserId() {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}") || {};
    return user.user_id ?? user.id ?? "";
  } catch {
    return "";
  }
}

export default function SettingsGear({ color = "#1A3584", size = 20 }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [err, setErr] = useState("");
  const [setting, setSetting] = useState(null);
  const [pdpaOn, setPdpaOn] = useState(true);
  const [pdpaUpdating, setPdpaUpdating] = useState(false);

  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("auth_token") ||
    "";

  const headers = useMemo(
    () => ({
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    }),
    [token]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await axios.get(ENDPOINT_GET_SETTINGS, {
        headers,
        timeout: 20000,
      });
      axios
        .get(ENDPOINT_GET_PDPA, { headers, timeout: 20000 })
        .then((r) => {
          if (r?.data?.success) setPdpaOn(!!r.data.data?.enabled);
        })
        .catch(() => {});
      if (data?.success && Array.isArray(data?.data)) {
        setSetting(pickLearnDoSetting(data.data));
      } else {
        setSetting(null);
        setErr(data?.message || "Failed to load settings.");
      }
    } catch {
      setSetting(null);
      setErr("Server error while loading settings.");
    } finally {
      setLoading(false);
    }
  }, [headers]);

  const openModal = () => {
    setOpen(true);
    load();
  };

  const updateAI = async (next) => {
    if (!setting?.id) {
      setErr("Cannot update: module setting row not found.");
      return;
    }
    setUpdating(true);
    setErr("");
    try {
      const { data } = await axios.post(
        ENDPOINT_UPDATE_SETTING,
        {
          id: setting.id,
          isEnabled: next ? 1 : 0,
          isForAll: setting.isForAll === 1 || setting.isForAll === "1" ? 1 : 0,
          user_id: getUserId() || setting.user_id || 0,
        },
        { headers, timeout: 20000 }
      );
      if (data?.success) {
        await load();
      } else {
        setErr(data?.message || "Failed to update setting.");
      }
    } catch {
      setErr("Server error while updating setting.");
    } finally {
      setUpdating(false);
    }
  };

  const updatePdpa = async (next) => {
    if (
      !next &&
      !window.confirm(
        "Turn off Sensitive Data Protection? OpenAI requests will no longer include the PDPA / privacy safeguards (no personal identifiers, no retention). Only do this if you are sure."
      )
    ) {
      return;
    }
    setPdpaUpdating(true);
    setErr("");
    try {
      const { data } = await axios.post(
        ENDPOINT_UPDATE_PDPA,
        { enabled: next },
        { headers, timeout: 20000 }
      );
      if (data?.success) {
        setPdpaOn(!!data.data?.enabled);
      } else {
        setErr(data?.message || "Failed to update PDPA protection.");
      }
    } catch (e) {
      setErr(e?.response?.data?.message || "Server error while updating PDPA protection.");
    } finally {
      setPdpaUpdating(false);
    }
  };

  const aiEnabled = setting?.isEnabled === 1 || setting?.isEnabled === "1";

  return (
    <>
      <Gear
        size={size}
        color={color}
        style={{ cursor: "pointer", outline: "none" }}
        onClick={openModal}
        role="button"
        aria-label="Settings"
        title="Settings"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") openModal();
        }}
      />

      <Modal
        show={open}
        onHide={() => setOpen(false)}
        centered
        className="custom-font"
        style={{ fontFamily: '"Manrope", sans-serif' }}
      >
        <Modal.Header closeButton style={{ "--bs-btn-close-opacity": 1 }}>
          <Modal.Title style={{ fontSize: 18, fontWeight: 700 }}>Settings</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {err ? (
            <Alert variant="warning" className="py-2">
              {err}
            </Alert>
          ) : null}

          <div
            className="d-flex align-items-center justify-content-between"
            style={{
              border: "1px solid #E5E7EB",
              borderRadius: 12,
              padding: "12px 14px",
            }}
          >
            <div className="d-flex align-items-center" style={{ gap: 12 }}>
              <Robot size={22} color="#183B72" />
              <div>
                <div style={{ fontWeight: 700 }}>Enable AI</div>
                <div style={{ fontSize: 13, color: "#111827" }}>
                  Use AI to analyse uploaded Learn + Do documents.
                </div>
              </div>
            </div>
            {loading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <Form.Check
                type="switch"
                id="settings-toggle-ai"
                checked={!!aiEnabled}
                disabled={updating || !setting?.id}
                onChange={(e) => updateAI(e.target.checked)}
              />
            )}
          </div>
          <div
            className="d-flex align-items-center justify-content-between"
            style={{
              border: "1px solid #E5E7EB",
              borderRadius: 12,
              padding: "12px 14px",
              marginTop: 12,
            }}
          >
            <div className="d-flex align-items-center" style={{ gap: 12 }}>
              <ShieldLock size={22} color="#183B72" />
              <div>
                <div style={{ fontWeight: 700 }}>Sensitive Data Protection</div>
                <div style={{ fontSize: 13, color: "#111827" }}>
                  Adds PDPA and security safeguards to every OpenAI request.
                </div>
              </div>
            </div>
            <Form.Check
              type="switch"
              id="settings-toggle-pdpa"
              checked={pdpaOn}
              disabled={pdpaUpdating || loading}
              onChange={(e) => updatePdpa(e.target.checked)}
            />
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}
